/**
 * Admin Rewards Dashboard — iter 43.
 *
 * Read-only observability + two mutation tools:
 *   • Manual adjustment    — append a ledger entry (super_admin)
 *   • Unlock account       — clear rewards_locked_at (admin+)
 *
 * Cards: overall liability, lifetime redeemed, provider health, locked users.
 * Tables: top redeemers, recent orders (with status chips), locked accounts.
 */
import { useCallback, useEffect, useState } from "react";
import {
  PiggyBank, Gift, ShieldAlert, Users, TrendingDown, Radio,
  Sparkles, RefreshCw, Unlock, Plus, Minus, X,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { cad } from "@/lib/pricing";
import { label as gcLabel } from "@/components/admin/giftcards/shared";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

function fmt(minor, currency = "CAD") {
  return currency === "CAD" ? cad((minor || 0) / 100)
    : ((minor || 0) / 100).toLocaleString("en-CA", { style: "currency", currency });
}

const STATUS_TINT = {
  FULFILLED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  PENDING:   "bg-amber-50 text-amber-700 border-amber-200",
  AWAITING_FULFILLMENT: "bg-amber-50 text-amber-700 border-amber-200",
  PROCESSING: "bg-cyan-50 text-cyan-700 border-cyan-200",
  NEEDS_REVIEW: "bg-orange-50 text-orange-700 border-orange-200",
  CASHBACK_RESTORED: "bg-slate-100 text-slate-600 border-slate-200",
  FAILED:    "bg-rose-50 text-rose-700 border-rose-200",
  VOIDED:    "bg-slate-100 text-slate-600 border-slate-200",
};

export default function AdminRewards() {
  const [overview, setOverview] = useState(null);
  const [topRedeemers, setTop] = useState([]);
  const [orders, setOrders] = useState([]);
  const [locked, setLocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [unlockTarget, setUnlockTarget] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [o, t, r, l] = await Promise.all([
        api.get("/admin/rewards/overview"),
        api.get("/admin/rewards/top-redeemers?days=30&limit=10"),
        api.get("/admin/rewards/orders?limit=30"),
        api.get("/admin/rewards/locked-users"),
      ]);
      setOverview(o.data);
      setTop(t.data.items || []);
      setOrders(r.data.items || []);
      setLocked(l.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load rewards dashboard");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (loading && !overview) {
    return <div className="p-8 text-sm text-slate-500" data-testid="admin-rewards-loading">Loading rewards dashboard…</div>;
  }
  const kpis = overview?.kpis || {};
  const health = overview?.provider_health_24h || {};

  return (
    <div className="p-6 space-y-6" data-testid="admin-rewards">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="pf-chip pf-chip-cyan w-fit"><PiggyBank className="w-3 h-3" /> Rewards operations</div>
          <h1 className="font-display text-2xl md:text-3xl text-slate-900 mt-2">Rewards dashboard</h1>
          <p className="text-xs text-slate-500 mt-1">Balance liability, gift-card fulfilment, and fraud controls in one place.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/rewards/fulfilment" data-testid="admin-rewards-open-fulfilment"
                className="pf-btn-primary px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-2">
            <Gift className="w-3.5 h-3.5" /> Gift card fulfilment
            {kpis.open_orders ? <span className="tabular-nums">· {kpis.open_orders} open{kpis.overdue_orders ? `, ${kpis.overdue_orders} overdue` : ""}</span> : null}
          </Link>
          <button onClick={reload}
                  className="px-3 py-1.5 rounded-full border border-slate-200 text-xs hover:bg-slate-50 inline-flex items-center gap-2"
                  data-testid="admin-rewards-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </header>

      {/* KPI band */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="Outstanding liability" value={fmt(kpis.outstanding_liability_minor)}
                 tone="from-rose-50 to-rose-100/60" accent="text-rose-700"
                 icon={TrendingDown} testid="admin-rewards-kpi-liability" />
        <KpiTile label="Lifetime redeemed" value={fmt(kpis.lifetime_redeemed_minor)}
                 tone="from-cyan-50 to-cyan-100/60" accent="text-cyan-700"
                 icon={Gift} testid="admin-rewards-kpi-redeemed" />
        <KpiTile label="Gift cards issued" value={(kpis.gift_cards_issued || 0).toLocaleString()}
                 tone="from-violet-50 to-violet-100/60" accent="text-violet-700"
                 icon={Sparkles} testid="admin-rewards-kpi-issued" />
        <KpiTile label="Locked accounts" value={(kpis.locked_accounts || 0).toLocaleString()}
                 tone={kpis.locked_accounts ? "from-amber-50 to-amber-100/60" : "from-slate-50 to-white"}
                 accent={kpis.locked_accounts ? "text-amber-700" : "text-slate-500"}
                 icon={ShieldAlert} testid="admin-rewards-kpi-locked" />
      </div>

      {/* Provider health */}
      <div className="pf-card p-5" data-testid="admin-rewards-health">
        <div className="flex items-center gap-2 mb-3">
          <Radio className="w-4 h-4 text-slate-500" />
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Provider health (last 24h)</div>
          <div className="ml-auto text-xs text-slate-500 tabular-nums">
            {((health.fulfilment_rate || 0) * 100).toFixed(1)}% fulfilment
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <HealthChip label="Total"     count={health.total || 0}     tone="bg-slate-50 text-slate-700 border-slate-200" />
          <HealthChip label="Fulfilled" count={health.fulfilled || 0} tone={STATUS_TINT.FULFILLED} />
          <HealthChip label="Pending"   count={health.pending || 0}   tone={STATUS_TINT.PENDING} />
          <HealthChip label="Failed"    count={health.failed || 0}    tone={STATUS_TINT.FAILED} />
          <HealthChip label="Voided"    count={health.voided || 0}    tone={STATUS_TINT.VOIDED} />
        </div>
      </div>

      {/* Top redeemers */}
      <div className="pf-card p-5" data-testid="admin-rewards-top-redeemers">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg text-slate-900">Top redeemers · last 30 days</h2>
          <div className="text-xs text-slate-500">{topRedeemers.length} shopper{topRedeemers.length === 1 ? "" : "s"}</div>
        </div>
        {topRedeemers.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-6">No redemptions in this window yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100">
                <th className="py-2 font-semibold">Shopper</th>
                <th className="py-2 font-semibold text-right">Orders</th>
                <th className="py-2 font-semibold text-right">Total redeemed</th>
                <th className="py-2 font-semibold text-right">Last order</th>
                <th className="py-2 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {topRedeemers.map((r) => (
                <tr key={r.user_id} className="border-b border-slate-50 hover:bg-slate-50/60"
                    data-testid={`admin-rewards-redeemer-${r.user_id}`}>
                  <td className="py-2 pr-2">
                    <div className="text-slate-900 font-medium">{r.email || r.user_id}</div>
                    {r.name && <div className="text-xs text-slate-500">{r.name}</div>}
                  </td>
                  <td className="py-2 text-right tabular-nums">{r.orders}</td>
                  <td className="py-2 text-right tabular-nums text-emerald-700 font-semibold">{r.total_display}</td>
                  <td className="py-2 text-right text-xs text-slate-500">{new Date(r.last_order_at).toLocaleDateString()}</td>
                  <td className="py-2 text-right">
                    <button onClick={() => setAdjustTarget({ user_id: r.user_id, email: r.email })}
                            className="text-xs text-cyan-700 hover:text-cyan-900 font-semibold"
                            data-testid={`admin-adjust-${r.user_id}`}>Adjust</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Recent orders */}
      <div className="pf-card p-5" data-testid="admin-rewards-orders">
        <h2 className="font-display text-lg text-slate-900 mb-3">Recent gift-card orders</h2>
        {orders.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-6">No orders yet.</div>
        ) : (
          <div className="space-y-1.5 max-h-96 overflow-y-auto">
            {orders.map((o) => (
              <div key={o.id} className="flex items-center gap-3 text-sm py-2 border-b border-slate-50 last:border-0"
                   data-testid={`admin-rewards-order-${o.id}`}>
                <div className={`w-2 h-2 rounded-full ${o.status === "FULFILLED" ? "bg-emerald-500" :
                                 o.status === "PENDING" ? "bg-amber-500" :
                                 o.status === "FAILED" ? "bg-rose-500" : "bg-slate-300"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-slate-900 font-medium truncate">{o.brand} · {fmt(o.denomination_minor)}</div>
                  <div className="text-xs text-slate-500 truncate">{o.shopper_email || o.user_id}</div>
                </div>
                <div className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold uppercase tracking-widest ${STATUS_TINT[o.status] || "bg-slate-50 border-slate-200 text-slate-600"}`}>
                  {gcLabel(o.status)}
                </div>
                <div className="text-xs text-slate-400 tabular-nums w-20 text-right">
                  {new Date(o.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Locked accounts */}
      {locked.length > 0 && (
        <div className="pf-card p-5 bg-gradient-to-br from-amber-50/40 to-white border-amber-200/40" data-testid="admin-rewards-locked">
          <h2 className="font-display text-lg text-slate-900 mb-3 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-600" /> Currently-locked accounts
          </h2>
          <div className="space-y-2">
            {locked.map((u) => (
              <div key={u.id} className="flex items-center gap-3 text-sm py-2 border-b border-slate-50 last:border-0"
                   data-testid={`admin-rewards-locked-${u.id}`}>
                <Users className="w-4 h-4 text-amber-600" />
                <div className="flex-1 min-w-0">
                  <div className="text-slate-900 font-medium truncate">{u.email}</div>
                  <div className="text-xs text-slate-500">Locked at {new Date(u.rewards_locked_at).toLocaleString()}</div>
                </div>
                <button onClick={() => setUnlockTarget(u)}
                        className="text-xs px-3 py-1 rounded-full border border-amber-200 text-amber-800 hover:bg-amber-100 font-semibold inline-flex items-center gap-1"
                        data-testid={`admin-unlock-${u.id}`}>
                  <Unlock className="w-3 h-3" /> Unlock
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <AdjustModal open={!!adjustTarget}
                    onOpenChange={(o) => !o && setAdjustTarget(null)}
                    target={adjustTarget}
                    onDone={() => { setAdjustTarget(null); reload(); }} />
      <UnlockModal open={!!unlockTarget}
                    onOpenChange={(o) => !o && setUnlockTarget(null)}
                    target={unlockTarget}
                    onDone={() => { setUnlockTarget(null); reload(); }} />
    </div>
  );
}

function KpiTile({ label, value, tone, accent, icon: Icon, testid }) {
  return (
    <div className={`pf-card p-4 bg-gradient-to-br ${tone}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <Icon className="w-4 h-4 text-slate-400" />
      </div>
      <div className={`font-display text-2xl md:text-3xl font-medium tabular-nums mt-2 ${accent}`}>{value}</div>
    </div>
  );
}

function HealthChip({ label, count, tone }) {
  return (
    <div className={`px-3 py-2 rounded-lg border ${tone} flex items-center justify-between`}>
      <div className="text-[10px] uppercase tracking-widest font-semibold">{label}</div>
      <div className="font-display text-lg tabular-nums">{count}</div>
    </div>
  );
}

function AdjustModal({ open, onOpenChange, target, onDone }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [direction, setDirection] = useState("credit");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!target || !amount || !reason || reason.length < 3) {
      toast.error("Amount + reason (min 3 chars) are required.");
      return;
    }
    setSubmitting(true);
    const signedAmount = direction === "debit" ? -Math.abs(parseFloat(amount)) : Math.abs(parseFloat(amount));
    try {
      await api.post("/admin/rewards/manual-adjustment", {
        user_id: target.user_id, amount: signedAmount, reason,
      });
      toast.success("Adjustment recorded on the ledger.");
      setAmount(""); setReason(""); setDirection("credit");
      onDone?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.detail || "Adjustment failed");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="admin-adjust-modal">
        <DialogHeader>
          <DialogTitle>Manual balance adjustment</DialogTitle>
          <DialogDescription>
            Every adjustment appends an immutable ledger entry with your admin id + reason.
          </DialogDescription>
        </DialogHeader>
        {target && (
          <div className="text-sm text-slate-600">Shopper: <span className="font-semibold text-slate-900">{target.email || target.user_id}</span></div>
        )}
        <div className="flex gap-2 mt-2">
          <button onClick={() => setDirection("credit")}
                  className={`flex-1 px-3 py-2 rounded-lg border text-xs font-semibold inline-flex items-center justify-center gap-1
                    ${direction === "credit" ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-white border-slate-200 text-slate-600"}`}
                  data-testid="admin-adjust-credit">
            <Plus className="w-3 h-3" /> Credit
          </button>
          <button onClick={() => setDirection("debit")}
                  className={`flex-1 px-3 py-2 rounded-lg border text-xs font-semibold inline-flex items-center justify-center gap-1
                    ${direction === "debit" ? "bg-rose-100 border-rose-300 text-rose-800" : "bg-white border-slate-200 text-slate-600"}`}
                  data-testid="admin-adjust-debit">
            <Minus className="w-3 h-3" /> Debit
          </button>
        </div>
        <label className="text-xs text-slate-500 font-semibold uppercase tracking-widest mt-3">Amount (CAD)</label>
        <input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
               className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm tabular-nums"
               placeholder="10.00" data-testid="admin-adjust-amount" />
        <label className="text-xs text-slate-500 font-semibold uppercase tracking-widest mt-3">Reason (required)</label>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  placeholder="e.g. Manual goodwill credit — ticket #4213"
                  data-testid="admin-adjust-reason" />
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={() => onOpenChange(false)}
                  className="px-4 py-2 rounded-full text-sm border border-slate-200 hover:bg-slate-50">Cancel</button>
          <button onClick={submit} disabled={submitting}
                  className="pf-btn-primary px-5 py-2 rounded-full text-sm font-semibold disabled:opacity-60"
                  data-testid="admin-adjust-submit">
            {submitting ? "Applying…" : "Apply adjustment"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UnlockModal({ open, onOpenChange, target, onDone }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!target || reason.length < 3) { toast.error("Reason required (min 3 chars)."); return; }
    setSubmitting(true);
    try {
      await api.post(`/admin/rewards/unlock/${target.id}`, { reason });
      toast.success("Account unlocked.");
      setReason("");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unlock failed");
    } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="admin-unlock-modal">
        <DialogHeader>
          <DialogTitle>Unlock rewards account</DialogTitle>
          <DialogDescription>
            Clears the fraud lock so this shopper can redeem again. Audit-logged.
          </DialogDescription>
        </DialogHeader>
        {target && <div className="text-sm text-slate-600">Shopper: <span className="font-semibold text-slate-900">{target.email}</span></div>}
        <label className="text-xs text-slate-500 font-semibold uppercase tracking-widest mt-3">Reason</label>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  placeholder="e.g. verified with shopper via ticket #4241"
                  data-testid="admin-unlock-reason" />
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-full text-sm border border-slate-200 hover:bg-slate-50">Cancel</button>
          <button onClick={submit} disabled={submitting}
                  className="pf-btn-primary px-5 py-2 rounded-full text-sm font-semibold disabled:opacity-60"
                  data-testid="admin-unlock-submit">
            {submitting ? "Unlocking…" : "Unlock account"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
